import { useState } from 'react'
import { Cargando } from '../componentes/Cargando'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { euros, fechaCorta } from '../componentes/formato'
import { AvisoError, CabeceraModal, CabeceraPagina, Campo, CLASE_BOTON, CLASE_BOTON_PELIGRO, CLASE_INPUT, Insignia } from '../componentes/formulario'
import { IconoDescargar } from '../componentes/iconos'
import { Modal } from '../componentes/Modal'
import { SelectorCliente } from '../componentes/SelectorCliente'
import { SkeletonList } from '../componentes/Skeleton'
import type { Albaran } from '../api/modelos'
import { BarraPaginacion } from '../componentes/BarraPaginacion'
import { contarPaginas } from '../componentes/paginacion'
import { TAMANO_ALBARANES, useAlbaran, useAlbaranes, useAnularAlbaran, usePdfAlbaran, type FiltrosAlbaranes } from '../hooks/useAlbaranes'
import { useEsAdmin } from '../hooks/useUsuarioActual'

function InsigniaAlbaran({ estado }: { estado: Albaran['estado'] }) {
  return estado === 'anulado' ? <Insignia tono="critico">Anulado</Insignia> : <Insignia tono="positivo">Emitido</Insignia>
}

function Detalle({ id, onCerrar }: { id: string; onCerrar: () => void }) {
  const albaran = useAlbaran(id)
  const anular = useAnularAlbaran(id)
  const pdf = usePdfAlbaran()
  const admin = useEsAdmin()
  const [motivo, setMotivo] = useState('')
  const [pedirMotivo, setPedirMotivo] = useState(false)
  const a = albaran.data
  const titulo = a ? `Albarán ${a.numero}` : 'Albarán'

  return (
    <Modal titulo={titulo} onCerrar={onCerrar} anchoMax="max-w-2xl">
      <CabeceraModal titulo={titulo} onCerrar={onCerrar} />
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {albaran.isPending ? (
          <Cargando mensaje="Cargando el albarán" />
        ) : albaran.isError ? (
          <EstadoError error={albaran.error} onReintentar={() => void albaran.refetch()} />
        ) : a ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2 text-sm text-tinta-2">
              <InsigniaAlbaran estado={a.estado} />
              <span>{a.cliente ?? a.contact_id}</span>
              <span>· Reparto {fechaCorta(a.fecha_reparto)}</span>
            </div>
            {a.envio?.direccion ? (
              <p className="text-sm text-tinta-2">
                Entrega en {[a.envio.direccion, a.envio.cp, a.envio.ciudad].filter(Boolean).join(', ')}
              </p>
            ) : null}
            <table className="w-full text-sm">
              <thead className="border-b border-borde text-left text-xs uppercase tracking-wide text-tinta-apagada">
                <tr>
                  <th className="py-1.5 font-semibold">Producto</th>
                  <th className="py-1.5 text-right font-semibold">Uds.</th>
                  <th className="py-1.5 text-right font-semibold">Precio</th>
                  <th className="py-1.5 text-right font-semibold">Importe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borde-suave">
                {(a.lineas ?? []).map((l) => (
                  <tr key={l.linea}>
                    <td className="py-1.5 text-tinta">{l.nombre}</td>
                    <td className="py-1.5 text-right tabular-nums">{l.unidades}</td>
                    <td className="py-1.5 text-right tabular-nums">{euros(l.precio)}</td>
                    <td className="py-1.5 text-right tabular-nums">{euros(l.importe_base)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <dl className="ml-auto grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <dt className="text-tinta-apagada">Base</dt><dd className="text-right tabular-nums">{euros(a.base)}</dd>
              <dt className="text-tinta-apagada">Impuesto</dt><dd className="text-right tabular-nums">{euros(a.cuota)}</dd>
              <dt className="font-semibold text-tinta">Total</dt><dd className="text-right font-semibold tabular-nums">{euros(a.total)}</dd>
            </dl>
            {a.nota_entrega ? <p className="rounded-lg bg-elevado px-3 py-2 text-sm text-tinta-2">Nota de entrega: {a.nota_entrega}</p> : null}
            {a.anulado ? (
              <p className="rounded-lg border border-critico-anillo bg-critico-fondo px-3 py-2 text-sm text-critico-tinta">
                Anulado{a.anulado.por ? ` por ${a.anulado.por}` : ''}{a.anulado.motivo ? `: ${a.anulado.motivo}` : ''}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <button type="button" className={CLASE_BOTON} disabled={pdf.isPending} onClick={() => pdf.mutate({ id: a.id, numero: a.numero })}>
                <IconoDescargar aria-hidden="true" className="size-4" />
                {pdf.isPending ? 'Preparando el PDF' : 'Ver PDF'}
              </button>
              {admin && a.estado === 'emitido' && !pedirMotivo ? (
                <button type="button" className={CLASE_BOTON_PELIGRO} onClick={() => setPedirMotivo(true)}>
                  Anular albarán
                </button>
              ) : null}
            </div>
            <AvisoError error={pdf.error} />

            {pedirMotivo ? (
              <form
                className="flex flex-col gap-2 rounded-lg border border-atencion-anillo p-3"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (motivo.trim()) anular.mutate({ motivo: motivo.trim() }, { onSuccess: () => setPedirMotivo(false) })
                }}
              >
                <Campo etiqueta="Motivo de la anulación *" ayuda="Queda en el albarán. La fecha del cliente queda libre para otro.">
                  <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} maxLength={500} className={CLASE_INPUT} />
                </Campo>
                <AvisoError error={anular.error} />
                <div className="flex gap-2">
                  <button type="submit" className={CLASE_BOTON_PELIGRO} disabled={!motivo.trim() || anular.isPending}>
                    {anular.isPending ? 'Anulando' : 'Confirmar anulación'}
                  </button>
                  <button type="button" className={CLASE_BOTON} onClick={() => setPedirMotivo(false)}>Cancelar</button>
                </div>
              </form>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

/**
 * Albaranes internos (RF-43): lista por fecha y cliente, detalle, PDF y
 * anulación con motivo (solo admin). Un albarán emitido no se edita.
 */
export function Albaranes() {
  const [f, setF] = useState<FiltrosAlbaranes>({ desde: '', hasta: '', cliente: '', estado: '', page: 1 })
  const [cliente, setCliente] = useState<{ id: string; nombre: string } | null>(null)
  const [abierto, setAbierto] = useState<string | null>(null)
  const lista = useAlbaranes(f)
  const items = lista.data?.items ?? []
  const paginas = contarPaginas(lista.data?.total ?? 0, TAMANO_ALBARANES)
  const poner = (cambio: Partial<FiltrosAlbaranes>) => setF((a) => ({ ...a, ...cambio, page: cambio.page ?? 1 }))

  return (
    <div className="flex flex-col gap-4">
      <CabeceraPagina titulo="Albaranes" descripcion="Los albaranes que crea Publicar. Abre uno para ver sus líneas, sacar el PDF o anularlo." />

      <div className="grid gap-3 rounded-xl border border-borde bg-superficie p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Campo etiqueta="Desde">
          <input type="date" value={f.desde} onChange={(e) => poner({ desde: e.target.value })} className={CLASE_INPUT} />
        </Campo>
        <Campo etiqueta="Hasta">
          <input type="date" value={f.hasta} onChange={(e) => poner({ hasta: e.target.value })} className={CLASE_INPUT} />
        </Campo>
        <div className="flex flex-col gap-1 text-xs font-medium text-tinta-2">
          <span>Cliente</span>
          <SelectorCliente
            valor={cliente}
            onCambiar={(c) => {
              setCliente(c)
              poner({ cliente: c?.id ?? '' })
            }}
          />
        </div>
        <Campo etiqueta="Estado">
          <select value={f.estado} onChange={(e) => poner({ estado: e.target.value as FiltrosAlbaranes['estado'] })} className={CLASE_INPUT}>
            <option value="">Todos</option>
            <option value="emitido">Emitidos</option>
            <option value="anulado">Anulados</option>
          </select>
        </Campo>
      </div>

      {lista.isPending ? (
        <SkeletonList />
      ) : lista.isError ? (
        <EstadoError error={lista.error} onReintentar={() => void lista.refetch()} />
      ) : items.length === 0 ? (
        <EstadoVacio titulo="No hay albaranes" descripcion="Los albaranes aparecen aquí al publicar el agendamiento." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-borde bg-superficie">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-borde text-xs uppercase tracking-wide text-tinta-apagada">
              <tr>
                <th className="px-4 py-2 font-semibold">Número</th>
                <th className="px-4 py-2 font-semibold">Reparto</th>
                <th className="px-4 py-2 font-semibold">Cliente</th>
                <th className="px-4 py-2 text-right font-semibold">Total</th>
                <th className="px-4 py-2 font-semibold">Estado</th>
                <th className="px-4 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borde-suave">
              {items.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-2 font-medium tabular-nums text-tinta">{a.numero}</td>
                  <td className="px-4 py-2 tabular-nums text-tinta-2">{fechaCorta(a.fecha_reparto)}</td>
                  <td className="px-4 py-2 text-tinta-2">{a.cliente ?? a.contact_id}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{euros(a.total)}</td>
                  <td className="px-4 py-2"><InsigniaAlbaran estado={a.estado} /></td>
                  <td className="px-4 py-2 text-right">
                    <button type="button" className={CLASE_BOTON} onClick={() => setAbierto(a.id)} aria-label={`Ver el albarán ${a.numero}`}>
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BarraPaginacion pagina={f.page} paginas={paginas} onCambiar={(page) => poner({ page })} />

      {abierto ? <Detalle id={abierto} onCerrar={() => setAbierto(null)} /> : null}
    </div>
  )
}
